import React, { useMemo, useCallback } from 'react';
import { 
    ProductionCapabilityRowProps
} from 'src/features/types/r2fTypes';
import { AddR2FNode } from './AddR2FNode';
import './styles.css';

/**
 * Component for displaying a production capability parent row in the R2F threshold table
 * In the new UI structure, this only shows PC name and Add Handling Tier functionality
 */
export const R2FProductionCapabilityRow: React.FC<ProductionCapabilityRowProps> = ({
    productionCapability,
    isExpanded,
    onToggleExpand,
    pendingChanges,
    onValueChange,
    onDelete,
    onRemovePendingPC,
    userCanEdit,
    warehouseId,
    availableTiers
}) => {
    // Check if this is a newly added PC
    const isNewlyAdded = productionCapability.id.startsWith('pending_');
    
    // Check if this is Default_Network_PC and if editing should be restricted
    const isDefaultNetworkPC = productionCapability.name === 'Default_Network_PC';
    
    // Default_Network_PC can only be edited when on the DEFAULT warehouse page
    // For all other production capabilities, editing is allowed based on userCanEdit permissions
    // This ensures FC-specific pages cannot modify Default_Network_PC thresholds
    const canEditDefaultNetworkPC = !isDefaultNetworkPC || (warehouseId === 'DEFAULT');
    
    // Determine effective userCanEdit based on restrictions
    const effectiveUserCanEdit = userCanEdit && canEditDefaultNetworkPC;
    
    // Get existing node IDs to pass to AddR2FNode
    const existingHandlingTiers = useMemo(() => [
        ...(productionCapability.defaultNode ? [productionCapability.defaultNode.handlingTier] : []),
        ...productionCapability.additionalNodes.map(node => node.handlingTier)
    ], [productionCapability.defaultNode, productionCapability.additionalNodes]);
    
    // Determine the row class based on pending changes and default type
    const rowClass = useMemo(() => {
        let classes = '';
        
        // Check if this is a pending custom PC (added through the AddProductionCapability)
        if (productionCapability.id.startsWith('pending_')) {
            classes += 'add-pending ';
        }
        
        if (productionCapability.isNetworkDefault) {
            classes += 'network-default ';
        } else if (productionCapability.isFCDefault) {
            classes += 'fc-default ';
        }
        
        return classes.trim();
    }, [productionCapability.id, productionCapability.isNetworkDefault, productionCapability.isFCDefault]);
    
    // Handle click on expand/collapse icon
    const handleExpandClick = useCallback(() => {
        onToggleExpand(productionCapability.id);
    }, [onToggleExpand, productionCapability.id]);
    
    // Handle PC deletion - check if it's a pending PC or an existing one
    const handleDeletePC = useCallback(() => {
        console.log("Delete button clicked for PC:", productionCapability.name);
        
        // Check if this is a pending PC that hasn't been saved yet
        if (productionCapability.id.startsWith('pending_') && onRemovePendingPC) {
            // For pending PCs, completely remove them from the UI
            console.log("Removing pending PC:", productionCapability.name);
            onRemovePendingPC(productionCapability.name);
        } else {
            // For existing PCs, mark them for deletion
            // Create a synthetic default node ID if needed
            const defaultNodeId = productionCapability.defaultNode?.id || `${productionCapability.id}_default`;
            
            // Mark the PC for deletion by adding a delete action
            // For PCs without a default node, we'll create a synthetic delete action
            onValueChange(
                productionCapability.id,
                defaultNodeId,
                0, // These values don't matter for delete operations
                0, // These values don't matter for delete operations
                'Delete',
                'UI Delete', // reason
                'Newton Dashboard' // actionSource
            );
            
            // For additional nodes, add delete actions for each
            productionCapability.additionalNodes.forEach(node => {
                onValueChange(
                    productionCapability.id,
                    node.id, 
                    0, // These values don't matter for delete operations
                    0, // These values don't matter for delete operations
                    'Delete',
                    'UI Delete', // reason
                    'Newton Dashboard' // actionSource
                );
            });
        }
    }, [productionCapability, onValueChange, onRemovePendingPC]);

    // Handle adding a node to this production capability - acts as an adapter between AddR2FNode and onValueChange
    const handleAddNode = useCallback((productionCapabilityId: string, handlingTier: string) => {
        // Default values for newly added tiers
        const defaultLowThreshold = 0;
        const defaultHighThreshold = 100;
        
        console.log(`🔴 [R2F-ROW] START Adding handling tier ${handlingTier} to productionCapability ${productionCapabilityId}`);
        console.log(`🔴 [R2F-ROW] ProductionCapability data:`, productionCapability);
        
        try {
            // Important! Don't alter the handlingTier when passing to onValueChange
            // The handlingTier should be the actual handling tier name (e.g. "Default")
            console.log(`🔴 [R2F-ROW] Calling onValueChange with:
                productionCapabilityId=${productionCapabilityId},
                handlingTier=${handlingTier},
                low=${defaultLowThreshold},
                high=${defaultHighThreshold},
                action=Add`);
            
            onValueChange(
                productionCapabilityId,
                handlingTier, // Use the exact handlingTier from the dropdown, don't modify it
                defaultLowThreshold,
                defaultHighThreshold,
                'Add',
                `Adding ${handlingTier} handling tier`, // Add reason for audit log
                'Newton Dashboard' // Add action source for tracking
            );
            
            console.log(`✅ [R2F-ROW] Successfully called onValueChange for ${handlingTier}`);
            return true;
        } catch (err) {
            console.error(`❌ [R2F-ROW] ERROR calling onValueChange for ${handlingTier}:`, err);
            return false;
        }
    }, [onValueChange, productionCapability]);
    
    return (
        <tr className={`production-capability-row ${rowClass}`}>
            <td className="expand-header" onClick={handleExpandClick}>
                <span className="expand-icon" title={isExpanded ? "Collapse" : "Expand"}>
                    {isExpanded ? '▼' : '►'}
                </span>
            </td>
            <td>
                {productionCapability.name}
                {(productionCapability.isNetworkDefault || productionCapability.isFCDefault) && (
                    <span className={`default-badge ${productionCapability.isNetworkDefault ? 'network-badge' : 'fc-badge'}`}>
                        {productionCapability.isNetworkDefault ? 'Network' : 'FC'}
                    </span>
                )}
            </td>
            <td>
                {/* Empty handling tier cell for parent row */}
            </td>
            <td colSpan={2}>
                {/* No content in threshold cells for parent rows */}
            </td>
            <td>
                {/* Last Updated - Empty for parent rows */}
            </td>
            <td>
                {/* User - Empty for parent rows */}
            </td>
            {userCanEdit && (
                <td className="actions-cell">
                    {/* Add Handling Tier button - restricted for Default_Network_PC on non-DEFAULT warehouses */}
                    {isExpanded && (
                        <AddR2FNode
                            productionCapabilityId={productionCapability.id}
                            onAdd={handleAddNode}
                            userCanEdit={effectiveUserCanEdit}
                            existingHandlingTiers={existingHandlingTiers}
                            buttonStyle="compact" // Add a prop to request compact styling
                            availableTiers={availableTiers}
                        />
                    )}
                    
                    {/* Delete Production Capability button - restricted for Default_Network_PC on non-DEFAULT warehouses */}
                    <button
                        className="delete-tier-button"
                        onClick={handleDeletePC}
                        disabled={!effectiveUserCanEdit}
                        title={effectiveUserCanEdit ? "Delete this production capability" : "Default_Network_PC can only be edited on the DEFAULT warehouse page"}
                    >
                        🗑️
                    </button>
                </td>
            )}
        </tr>
    );
};
